import { useRef } from 'react';
import { useDialogA11y } from '../hooks/useDialogA11y.js';

export function Methodology({ open, onClose, auditDate, discovery, catalog }) {
  const modalRef = useRef(null);
  useDialogA11y(open, modalRef, onClose);

  if (!open) return null;
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section ref={modalRef} className="methodology-modal" role="dialog" aria-modal="true" aria-labelledby="methodology-title" tabIndex="-1">
        <button className="drawer-close" onClick={onClose} aria-label="Close methodology">×</button>
        <span className="eyebrow">Evidence before estimates</span>
        <h2 id="methodology-title">How to read AssetGlobe</h2>
        <p className="methodology-intro">This is a public-ledger observatory, not a valuation terminal. It reports what can be tied to a defensible XRPL identity and keeps claims separate from ledger facts.</p>

        <div className="methodology-grid">
          <article>
            <span>01</span>
            <h3>Identity</h3>
            <p><b>Verified</b> means a first-party source publishes the account. <b>Corroborated</b> joins a first-party program announcement to consistent ledger and institutional registry evidence. Announced programs remain wallet-pending.</p>
          </article>
          <article>
            <span>02</span>
            <h3>Balances</h3>
            <p>Classic-token obligations come from <code>gateway_balances</code>. MPT outstanding amounts come from the issuance ledger entry. Units are never converted to USD without a defensible price and unit definition.</p>
          </article>
          <article>
            <span>03</span>
            <h3>Transactions</h3>
            <p><b>LIVE</b> items arrive from XRPL’s validated transactions stream after this page connects. <b>HISTORY</b> items come from recent <code>account_tx</code> results for tracked public accounts. Failed transactions are excluded.</p>
          </article>
          <article>
            <span>04</span>
            <h3>Reference catalog</h3>
            <p>{catalog.status === 'ready' ? <>The authorized RWA.xyz layer contains <b>{catalog.meta?.distinctAssets || '—'} distinct assets</b> and {catalog.meta?.tokenRecords || '—'} XRPL token records. Exact public pairs can match live transactions, but third-party classification is not promoted to verified evidence.</> : <>The RWA.xyz layer is fail-closed until an authorized API key and public-display permission are configured. No scraped fallback is published.</>}</p>
          </article>
          <article>
            <span>05</span>
            <h3>Discovery</h3>
            <p>XRPLMeta currently indexes {discovery.totalMpts || 'hundreds of'} MPT issuances; {discovery.programs.length || 'some'} self-label as RWA or stablecoin. These appear only in the Metadata view and are not endorsements or backing verification.</p>
          </article>
          <article>
            <span>06</span>
            <h3>Coverage audit</h3>
            <p>Catalog pages are fetched server-side and reduced to an explicit public-field allowlist. A separate mainnet audit checks every exact issuer/currency pair with <code>gateway_balances</code>; account-only and zero-balance records keep narrower claims.</p>
          </article>
        </div>

        <div className="methodology-caveat">
          <strong>Coverage boundary</strong>
          <p>There is no canonical global RWA registry on XRPL. RWA.xyz catalog coverage, AssetGlobe’s independently curated identities, and self-published MPT metadata are different evidence layers. Issuers can also use multiple accounts, private or permissioned venues, and unpublished identities. “No public identity found” is not the same as “does not exist.” Geography marks the associated organization’s headquarters, not transaction origin, wallet location or asset situs.</p>
        </div>

        <div className="methodology-footer">
          <span>Registry researched through {auditDate}</span>
          <div>
            <a href="https://xrpl.org/docs/references/http-websocket-apis/public-api-methods/subscription-methods/subscribe" target="_blank" rel="noreferrer">XRPL stream docs ↗</a>
            <a href="https://xrpl.org/docs/references/protocol/data-types/currency-formats" target="_blank" rel="noreferrer">Token formats ↗</a>
            <a href="https://xrplmeta.org/docs/rest/list-tokens" target="_blank" rel="noreferrer">XRPLMeta API ↗</a>
            <a href="https://app.rwa.xyz/networks/xrp-ledger" target="_blank" rel="noreferrer">RWA.xyz XRPL catalog ↗</a>
          </div>
        </div>
      </section>
    </div>
  );
}
